import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Accordion, Button, Col, Container, Form, Row, Spinner, Alert } from 'react-bootstrap';
import { Trash } from 'react-bootstrap-icons';

import api from '../../services/api';
import { cleanObject } from '../../utils/commons.utils';
import CustomerFormFields from './customer-form-fields';
import { useAsyncCallback } from '../../hooks/useAsync';
import { useCustomerForm } from '../../hooks/useCustomerForm';
import { useNotification } from '../../contexts/NotificationContext';

const fieldLabels: Record<string, string> = {
  email: 'Email',
  firstName: 'Nombre(s)',
  lastName: 'Apellidos',
  rfc: 'RFC',
  phone: 'Teléfono',
  address: 'Dirección',
  zipCode: 'Código postal',
  state: 'Estado',
  city: 'Ciudad',
  addressNumber: 'Número exterior',
  neighborhood: 'Colonia',
  bankId: 'Banco',
  clabe: 'Clabe',
};

const CustomerNew: React.FC = () => {
  const navigate = useNavigate();
  const { error: showError } = useNotification();
  
  const {
    formData,
    updateField,
    updateBeneficiary,
    addBeneficiary,
    removeBeneficiary,
    addDocument,
    addBeneficiaryDocument,
    validateForm,
    setFormData,
  } = useCustomerForm();

  const [fileErrors, setFileErrors] = useState({
    identificationDoc: '',
    taxDoc: '',
    addressProof: '',
  });

  const { execute: createCustomer, loading } = useAsyncCallback(
    async (data: any) => {
      const response = await api.post('/users', data);
      return response.data;
    },
    'Cliente registrado exitosamente'
  );

  const validateFormFields = () => {
    const { isValid, errors } = validateForm();
    
    if (!isValid) {
      const errorMessage = errors.join('\n');
      showError('Errores de validación', errorMessage);
      return false;
    }

    return true;
  };

  const handleChange = (e: React.ChangeEvent<any>, index: number | null = null) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (index !== null) {
      updateBeneficiary(index, target.name, target.value);
    } else {
      updateField(target.name, target.value);
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<any>,
    documentTypeId: number,
    index: number | null,
  ) => {
    const { name, files } = e.target;
    if (files && files.length > 0) {
      const file = files[0];

      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setFileErrors({ ...fileErrors, [name]: 'Formato no válido. Solo PDF, JPG y PNG.' });
        return;
      }
      if (file.size > 3 * 1024 * 1024) {
        setFileErrors({ ...fileErrors, [name]: 'El archivo no debe pesar más de 3MB.' });
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1]; // Remueve prefijo MIME type
        
        const document = {
          documentTypeId,
          filename: file.name,
          file: base64String,
        };

        if (index !== null) {
          addBeneficiaryDocument(index, document);
        } else {
          addDocument(document);
        }

        setFileErrors({ ...fileErrors, [name]: '' });
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFormFields()) return;

    const data = cleanObject(formData);
    const result = await createCustomer(data);
    
    if (result) {
      navigate('/dashboard/customers');
    }
  };

  return (
    <Container>
      <Row className="mt-3 sticky-header">
        <Col md={2}>
          <Button variant="secondary" onClick={() => navigate('/dashboard/customers')}>
            Regresar
          </Button>
        </Col>
        <Col className="text-center" md={8}>
          <h2>Nuevo Cliente</h2>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={{ span: 8, offset: 2 }}>
          <Form onSubmit={handleSubmit}>
            <CustomerFormFields
              formData={formData}
              handleChange={handleChange}
              handleFileChange={handleFileChange}
            />

            <Container fluid>
              <Row className="mb-3 mt-5 form-title">
                <Col md={6}>
                  <h4>Beneficiarios</h4>
                </Col>
                <Col md={6}>
                  <Button size="sm" onClick={addBeneficiary}>
                    + Agregar
                  </Button>
                </Col>
              </Row>
            </Container>

            <Accordion className="mt-3">
              {formData.beneficiaries.map((beneficiary, index) => (
                <Accordion.Item eventKey={index.toString()} key={index}>
                  <Accordion.Header>
                    <Button
                      variant="danger"
                      size="sm"
                      className="me-3"
                      onClick={() => removeBeneficiary(index)}
                    >
                      <Trash />
                    </Button>
                    <span>Beneficiario {index + 1}</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <CustomerFormFields
                      key={index}
                      index={index}
                      formData={formData}
                      handleChange={handleChange}
                      handleFileChange={handleFileChange}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>

            <Row className="sticky-footer">
              <Col className="text-right">
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Guardando...
                    </>
                  ) : (
                    'Guardar'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default CustomerNew;
